import React, {useEffect, useRef, useState} from 'react'
import {Button, Card, Col, Form, InputGroup, Row, Tab, Tabs} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import Select from 'react-select'
import {useQuery, useLazyQuery, useMutation, useApolloClient} from '@apollo/client'
import {toast} from 'react-toastify'
import {useDebounce} from '../../../../../_metronic/helpers'
import {parseId} from '../../../helpers'
import {
  GET_ALL_SIGNATURE_FORMS_ACTIVE,
  RENDER_SIGNATURE_FORM_PREVIEW,
} from '../../../gql/queries/signatureFormsApiQueries'
import {GET_ALL_USERS_FILTER} from '../../../gql/queries/usersQuery'
import {
  CREATE_SIGNATURE_FORMS_ENTRY,
  RESEND_SIGNATURE_FORM_ENTRY,
} from '../../../gql/mutations/signatureFormsMutations'
import {IResSignatureFormsAPI} from '../../../types/signatureFormsApi'
import {NodeSignatureFormsEntry} from '../../../types/signatureFormsEntriesApi'
import {GET_ALL_USERS_BASIC} from '../../../gql/queries/usersQuery'
import {useGetUser} from '../../../store/userStore'

type SelectedForm = {id: string; name: string}
type SendOptions = {
  sendSms: boolean
  shouldSendEmail: boolean
  getSigningLink: boolean
}

export const SignatureFormRequestContent: React.FC<{
  onClose: () => void
  refetch: () => void
  sourceType: string
  sourceId: string
  preAgentId?: string
  hasEmail?: boolean
  hasPhone?: boolean
  onPrev?: () => void
  resendEntryId?: NodeSignatureFormsEntry
}> = ({
  onClose,
  refetch,
  sourceType,
  sourceId,
  preAgentId,
  hasEmail,
  hasPhone,
  onPrev,
  resendEntryId,
}) => {
  const intl = useIntl()
  const apolloClient = useApolloClient()
  const authUser = useGetUser((s) => s.user)
  const userGroups = (authUser?.groups ?? []).map((group: any) => group?.name || group)
  const currentUserPrimaryGroup = authUser?.user?.primaryGroup?.name
  const isAdvisor = currentUserPrimaryGroup === 'Asesor' || userGroups.includes('Asesor')
  const canRequestSigningLink = !isAdvisor

  const [sendOptions, setSendOptions] = useState<SendOptions>({
    sendSms: Boolean(hasPhone),
    shouldSendEmail: Boolean(hasEmail),
    getSigningLink: canRequestSigningLink,
  })
  const [selectedForms, setSelectedForms] = useState<SelectedForm[]>([])
  const [previews, setPreviews] = useState<Record<string, string>>({})
  const [loadingPreviews, setLoadingPreviews] = useState<Record<string, boolean>>({})
  const [activeTab, setActiveTab] = useState<string>('')
  const [selectedAgent, setSelectedAgent] = useState<{label: string; value: string} | null>(null)
  const [urlLink, setUrlLink] = useState<string | null>(null)
  const [agentId, setAgentId] = useState<string | null>(preAgentId ?? null)
  const [createdOrSended, setCreatedOrSended] = useState(false)
  const [inputSelect, setInputSelect] = useState('')
  const valueDeb = useDebounce(inputSelect, 500)
  const [options, setOptions] = useState<{label: string; value: string}[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const [copied, setCopied] = useState(false)

  const {data: dataAllSignatureForms} = useQuery<IResSignatureFormsAPI>(
    GET_ALL_SIGNATURE_FORMS_ACTIVE,
    {fetchPolicy: 'no-cache'}
  )

  const [gqlGetMembers, {data: dataUsers, loading: loadingUsers}] = useLazyQuery(
    GET_ALL_USERS_FILTER,
    {fetchPolicy: 'no-cache'}
  )

  const [gqlGetMember, {data: dataUser}] = useLazyQuery(GET_ALL_USERS_BASIC)

  const userPreSeleccionado = dataUser?.allUsers.edges[0].node

  useEffect(() => {
    if (!userPreSeleccionado) return
    gqlGetMembers({variables: {name: userPreSeleccionado.firstName, requireNpn: true}})
  }, [gqlGetMembers, userPreSeleccionado])

  useEffect(() => {
    if (!valueDeb && !userPreSeleccionado) {
      setOptions([])
      return
    }
    if (!dataUsers) return
    setOptions(
      dataUsers.allUsers.edges.map(({node}: any) => ({
        label: `${node.firstName} ${node.lastName} - ${node.email}${
          node.npn ? ` (${node.npn})` : ''
        }`,
        value: node.id,
      }))
    )
  }, [dataUsers, userPreSeleccionado, valueDeb])

  useEffect(() => {
    if (!valueDeb) return
    gqlGetMembers({variables: {name: valueDeb, requireNpn: true}})
  }, [gqlGetMembers, valueDeb])

  useEffect(() => {
    if (!userPreSeleccionado) return
    const option = {
      label: `${userPreSeleccionado.firstName} ${userPreSeleccionado.lastName} - ${
        userPreSeleccionado.email
      }${userPreSeleccionado.npn ? ` (${userPreSeleccionado.npn})` : ''}`,
      value: userPreSeleccionado.id,
    }
    setSelectedAgent(option)
    setAgentId(userPreSeleccionado.id)
  }, [userPreSeleccionado])

  useEffect(() => {
    const targetId = resendEntryId?.agent?.id ?? resendEntryId?.user?.id ?? preAgentId
    if (!targetId) return
    setAgentId(targetId)
    gqlGetMember({variables: {id: Number(parseId(targetId))}})
  }, [gqlGetMember, resendEntryId, preAgentId])

  // Fetch previews for all selected forms whenever forms or agent changes
  useEffect(() => {
    if (selectedForms.length === 0) {
      setPreviews({})
      setLoadingPreviews({})
      return
    }

    const loading: Record<string, boolean> = {}
    selectedForms.forEach((f) => {
      loading[f.id] = true
    })
    setLoadingPreviews(loading)
    setPreviews({})

    selectedForms.forEach((form) => {
      apolloClient
        .query({
          query: RENDER_SIGNATURE_FORM_PREVIEW,
          variables: {
            formId: '' + parseId(form.id),
            sourceType,
            sourceId: String(parseId(sourceId) ?? 0),
            agentId: agentId ? String(parseId(agentId)) : '0',
          },
          fetchPolicy: 'no-cache',
        })
        .then((result) => {
          const html = result.data?.renderSignatureFormPreview?.renderedHtml ?? ''
          setPreviews((prev) => ({...prev, [form.id]: html}))
        })
        .catch(() => {
          setPreviews((prev) => ({...prev, [form.id]: ''}))
        })
        .finally(() => {
          setLoadingPreviews((prev) => ({...prev, [form.id]: false}))
        })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedForms, agentId])

  const [createSignatureEntry, {loading: loadingCreateSFE}] = useMutation(
    CREATE_SIGNATURE_FORMS_ENTRY
  )

  const [resendSignatureEntry, {data: dataResendEntry}] = useMutation(RESEND_SIGNATURE_FORM_ENTRY)

  useEffect(() => {
    setSendOptions({
      sendSms: Boolean(hasPhone || resendEntryId),
      shouldSendEmail: Boolean(hasEmail || resendEntryId),
      getSigningLink: canRequestSigningLink,
    })
    setCreatedOrSended(false)
    setUrlLink(null)
  }, [resendEntryId, hasEmail, hasPhone, canRequestSigningLink])

  useEffect(() => {
    if (canRequestSigningLink) return
    setSendOptions((prev) => (prev.getSigningLink ? {...prev, getSigningLink: false} : prev))
    setUrlLink(null)
  }, [canRequestSigningLink])

  useEffect(() => {
    const data = dataResendEntry?.resendSignatureFormEntry
    if (!data) return

    if (data.ok) {
      setCreatedOrSended(true)
      if (!data.emailSent && !data.signingUrl) {
        toast.warning(
          intl.formatMessage(
            {
              id: 'documents.signatureForms.request.toast.ignored',
              defaultMessage: 'The request was ignored. {detail}',
            },
            {
              detail:
                data.errorMessage ||
                intl.formatMessage({
                  id: 'documents.signatureForms.request.toast.noEmailNoLink',
                  defaultMessage: 'No email sent and no link requested.',
                }),
            }
          )
        )
        return
      }
      if (data.emailSent)
        toast.success(
          intl.formatMessage({
            id: 'documents.signatureForms.request.toast.emailSent',
            defaultMessage: 'Email sent successfully',
          })
        )
      if (data.signingUrl) setUrlLink(data.signingUrl)
      refetch()
    } else {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {
            message:
              data.errorMessage ||
              intl.formatMessage({
                id: 'documents.signatureForms.request.toast.somethingWrong',
                defaultMessage: 'Something went wrong',
              }),
          }
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataResendEntry])

  const handleCopy = () => {
    if (inputRef.current) {
      navigator.clipboard.writeText(inputRef.current.value).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 4500)
      })
    }
  }

  const handleCreateSFE = async () => {
    if (selectedForms.length === 0 || !agentId) return
    if (resendEntryId?.id) return

    try {
      const result = await createSignatureEntry({
        variables: {
          formIds: selectedForms.map((f) => String(parseId(f.id))),
          sourceType,
          sourceId: String(parseId(sourceId) ?? 0),
          agentId: String(parseId('' + agentId)),
          ...sendOptions,
          getSigningLink: canRequestSigningLink ? sendOptions.getSigningLink : false,
        },
      })

      const data = result.data?.createSignatureFormsEntry
      if (!data) return

      if (data.ok) {
        setCreatedOrSended(true)
        refetch()
        toast.success(
          intl.formatMessage({
            id: 'documents.signatureForms.request.toast.created',
            defaultMessage: 'Request consent created successfully.',
          })
        )
        if (data.errorMessage)
          toast.warning(
            intl.formatMessage(
              {
                id: 'documents.signatureForms.request.toast.warningWithMessage',
                defaultMessage: 'Warning: {message}',
              },
              {message: data.errorMessage}
            )
          )
        if (sendOptions.shouldSendEmail && data.emailSent === false)
          toast.warning(
            intl.formatMessage({
              id: 'documents.signatureForms.request.toast.emailNotSent',
              defaultMessage: 'Email could not be sent',
            })
          )
        if (!sendOptions.getSigningLink) onClose()
        else setUrlLink(data.signingLink)
      } else {
        toast.error(
          intl.formatMessage(
            {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
            {
              message:
                data.errorMessage ??
                intl.formatMessage({
                  id: 'documents.signatureForms.request.toast.createFailed',
                  defaultMessage: 'Something went wrong while creating consent request.',
                }),
            }
          )
        )
      }
    } catch {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {
            message: intl.formatMessage({
              id: 'documents.signatureForms.request.toast.createFailed',
              defaultMessage: 'Something went wrong while creating consent request.',
            }),
          }
        )
      )
    }
  }

  const handleResend = () => {
    if (!resendEntryId?.id) return
    resendSignatureEntry({
      variables: {
        entryId: '' + parseId(resendEntryId?.id),
        shouldSendEmail: sendOptions.shouldSendEmail,
        sendSms: sendOptions.sendSms,
        getSigningLink: canRequestSigningLink ? sendOptions.getSigningLink : false,
      },
    })
  }

  const handlePrintPDF = () => {
    const htmlParts: string[] = []

    if (resendEntryId) {
      if (resendEntryId.renderedHtml) htmlParts.push(resendEntryId.renderedHtml)
    } else {
      selectedForms.forEach((form) => {
        if (previews[form.id]) htmlParts.push(previews[form.id])
      })
    }

    if (htmlParts.length === 0) return

    const combined = htmlParts.join('<div style="page-break-after: always; margin: 0;"></div>')
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      toast.warning(
        intl.formatMessage({
          id: 'documents.signatureForms.request.toast.allowPopups',
          defaultMessage: 'Please allow pop-ups to generate the PDF.',
        })
      )
      return
    }

    const pdfDocTitle = intl.formatMessage({
      id: 'documents.signatureForms.request.pdf.docTitle',
      defaultMessage: 'Form preview',
    })

    printWindow.document.open()
    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${pdfDocTitle}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 24px; color: #222; }
      @media print { body { margin: 0; } }
    </style>
  </head>
  <body onload="window.print()">
    ${combined}
  </body>
</html>`)
    printWindow.document.close()
  }

  const isPrintPDFDisabled = resendEntryId
    ? !resendEntryId.renderedHtml
    : selectedForms.length === 0 || Object.values(loadingPreviews).some(Boolean)

  const canModifyChannels = !createdOrSended
  const canToggleLink = canRequestSigningLink && !createdOrSended

  const formOptions =
    dataAllSignatureForms?.allSignatureForms.edges.map(({node}: any) => ({
      value: node.id,
      label: node.name,
    })) ?? []

  return (
    <div className='request-consent'>
      <div className='filter-signature'>
        <Row>
          <Col lg={6}>
            <Form.Label>
              <FormattedMessage
                id='documents.signatureForms.request.field.forms'
                defaultMessage='Form signature'
              />
            </Form.Label>
            {resendEntryId ? (
              <Form.Control readOnly value={resendEntryId.signatureForm.name} />
            ) : (
              <Select
                isMulti
                isDisabled={createdOrSended}
                value={selectedForms.map((f) => ({value: f.id, label: f.name}))}
                options={formOptions}
                placeholder={intl.formatMessage({
                  id: 'documents.signatureForms.request.forms.placeholder',
                  defaultMessage: 'Select one or more forms...',
                })}
                onChange={(selected) => {
                  const forms = ((selected as {value: string; label: string}[]) ?? []).map((s) => ({
                    id: s.value,
                    name: s.label,
                  }))
                  setSelectedForms(forms)
                  const newIds = new Set(forms.map((f) => f.id))
                  const stillActive = newIds.has(activeTab)
                  if (forms.length > 0 && !stillActive) setActiveTab(forms[0].id)
                  if (forms.length === 0) setActiveTab('')
                }}
              />
            )}
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='common.field.agent'
                  defaultMessage='Agent'
                />
              </Form.Label>
              <Select
                value={selectedAgent}
                isDisabled={createdOrSended}
                isLoading={loadingUsers}
                placeholder={intl.formatMessage({
                  id: 'documents.signatureForms.request.agent.placeholder',
                  defaultMessage: 'Search and select an agent...',
                })}
                options={options}
                inputValue={inputSelect}
                onInputChange={setInputSelect}
                onChange={(selected) => {
                  setSelectedAgent(selected)
                  setAgentId(selected?.value ?? null)
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className='check-signature-forms'>
          <Col
            md={3}
            onClick={() => {
              if (!canModifyChannels) return
              setSendOptions((prev) => ({...prev, shouldSendEmail: !prev.shouldSendEmail}))
            }}
          >
            <Form.Check
              disabled={!canModifyChannels}
              label={intl.formatMessage({
                id: 'documents.signatureForms.request.option.sendEmail',
                defaultMessage: 'Send email',
              })}
              type='switch'
              id='send_email'
              checked={sendOptions.shouldSendEmail}
              onChange={(e) => {
                if (!canModifyChannels) return
                setSendOptions((prev) => ({...prev, shouldSendEmail: e.target.checked}))
              }}
            />
          </Col>

          <Col
            md={3}
            onClick={() => {
              if (!canModifyChannels) return
              setSendOptions((prev) => ({...prev, sendSms: !prev.sendSms}))
            }}
          >
            <Form.Check
              disabled={!canModifyChannels}
              label={intl.formatMessage({
                id: 'documents.signatureForms.request.option.sendSms',
                defaultMessage: 'Send SMS',
              })}
              type='switch'
              id='send_sms'
              checked={sendOptions.sendSms}
              onChange={(e) => {
                if (!canModifyChannels) return
                setSendOptions((prev) => ({...prev, sendSms: e.target.checked}))
              }}
            />
          </Col>

          <Col
            md={3}
            onClick={() => {
              if (!canToggleLink) return
              setSendOptions((prev) => ({...prev, getSigningLink: !prev.getSigningLink}))
            }}
          >
            <Form.Check
              disabled={!canToggleLink}
              label={intl.formatMessage({
                id: 'documents.signatureForms.request.option.getLink',
                defaultMessage: 'Get URL link',
              })}
              type='switch'
              id='getlink'
              checked={sendOptions.getSigningLink}
              onChange={(e) => {
                if (!canToggleLink) return
                setSendOptions((prev) => ({...prev, getSigningLink: e.target.checked}))
              }}
            />
          </Col>
        </Row>
      </div>

      {!urlLink ? (
        <Card className='card-form-request'>
          <Card.Body>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='common.action.preview'
                  defaultMessage='Preview'
                />
              </Form.Label>
              {resendEntryId ? (
                <div
                  className='custom-box'
                  dangerouslySetInnerHTML={{__html: resendEntryId.renderedHtml ?? ''}}
                />
              ) : selectedForms.length === 0 ? (
                <p className='text-muted'>
                  <FormattedMessage
                    id='documents.signatureForms.request.preview.empty'
                    defaultMessage='Select one or more forms to preview them here.'
                  />
                </p>
              ) : (
                <>
                  {selectedForms.length > 1 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                        padding: '6px 10px',
                        backgroundColor: '#eaf3fb',
                        border: '1px solid #b6d8f2',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#2980b9',
                      }}
                    >
                      <i className='bi bi-layers' style={{fontSize: '15px'}} />
                      <span>
                        <FormattedMessage
                          id='documents.signatureForms.request.preview.multiHint'
                          defaultMessage='<b>{count, plural, one {# document} other {# documents}}</b> — click each tab to preview'
                          values={{
                            count: selectedForms.length,
                            b: (chunks) => <strong>{chunks}</strong>,
                          }}
                        />
                      </span>
                    </div>
                  )}
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k ?? '')}
                    className='mb-3'
                    style={{
                      borderBottom: '2px solid #2980b9',
                      gap: '4px',
                    }}
                  >
                    {selectedForms.map((form, idx) => (
                      <Tab
                        key={form.id}
                        eventKey={form.id}
                        title={
                          <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                backgroundColor: activeTab === form.id ? '#fff' : '#2980b9',
                                color: activeTab === form.id ? '#2980b9' : '#fff',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                flexShrink: 0,
                              }}
                            >
                              {idx + 1}
                            </span>
                            {form.name}
                          </span>
                        }
                      >
                        {loadingPreviews[form.id] ? (
                          <p>
                            <FormattedMessage
                              id='documents.signatureForms.request.preview.loading'
                              defaultMessage='Loading preview...'
                            />
                          </p>
                        ) : previews[form.id] ? (
                          <div
                            className='custom-box'
                            dangerouslySetInnerHTML={{__html: previews[form.id]}}
                          />
                        ) : (
                          <p className='text-muted'>
                            <FormattedMessage
                              id='documents.signatureForms.request.preview.notAvailable'
                              defaultMessage='Preview not available.'
                            />
                          </p>
                        )}
                      </Tab>
                    ))}
                  </Tabs>
                </>
              )}
            </Form.Group>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header className='card-header-link'>
            <div>
              <FormattedMessage
                id='documents.signatureForms.request.shareLinkHint'
                defaultMessage='Share this link with your client if you want to send it by other means manually'
              />
            </div>
          </Card.Header>
          <Card.Body className='url-link'>
            <InputGroup className='mb-3'>
              <Form.Control ref={inputRef} value={urlLink ?? ''} readOnly />
              <Button variant='outline-secondary' onClick={handleCopy} className='search-cms-plan'>
                {copied ? (
                  <>
                    <i className='bi bi-clipboard-check' />
                    <span>
                      <FormattedMessage
                        id='documents.signatureForms.request.copied'
                        defaultMessage='Copied'
                      />
                    </span>
                  </>
                ) : (
                  <i className='bi bi-clipboard' />
                )}
              </Button>
            </InputGroup>
          </Card.Body>
        </Card>
      )}

      <div
        className={`${
          (sourceType === 'contact' && !onPrev) || sourceType === 'lead'
            ? 'btn-prev-next  btn-prev-next-end'
            : 'btn-prev-next'
        }`}
      >
        {onPrev &&
          (!createdOrSended ? (
            <Button size='sm' variant='outline' onClick={onPrev}>
              <FormattedMessage
                id='common.action.previous'
                defaultMessage='Previous'
              />
            </Button>
          ) : (
            <div></div>
          ))}
        <div className='d-flex align-items-center justify-content-between w-100'>
          <Button variant='secondary' size='sm' onClick={onClose} className='me-2'>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </Button>
          <div className='d-flex align-items-center gap-2'>
            <Button
              size='sm'
              onClick={handlePrintPDF}
              disabled={isPrintPDFDisabled}
              title={intl.formatMessage({
                id: 'documents.signatureForms.request.pdf.tooltip',
                defaultMessage: 'Open a print-ready preview to save as PDF',
              })}
            >
              <i className='bi bi-printer me-1' />
              <FormattedMessage
                id='documents.signatureForms.request.action.getPdf'
                defaultMessage='Get PDF'
              />
            </Button>
            {resendEntryId && !createdOrSended ? (
              <Button size='sm' onClick={handleResend} disabled={loadingCreateSFE}>
                <FormattedMessage
                  id='common.action.resend'
                  defaultMessage='Resend'
                />
              </Button>
            ) : (
              !createdOrSended && (
                <Button
                  size='sm'
                  onClick={handleCreateSFE}
                  disabled={selectedForms.length === 0 || !agentId || loadingCreateSFE}
                >
                  <FormattedMessage
                    id='common.action.send'
                    defaultMessage='Send'
                  />
                </Button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
